import { useState, useRef, useEffect, useCallback } from "react";
import { useT } from "@/i18n";
import { canvasCommentsMessages } from "@/i18n/messages/canvas-comments";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { useCanvasStore } from "@/stores/canvas-store";
import { useProjectStore } from "@/stores/project-store";
import { useAgentStore } from "@/stores/agent-store";
import { useTasksStore } from "@/stores/tasks-store";
import { fetchComments } from "@/api/tasks";
import type { TaskComment } from "@/api/tasks";
import { useAuth } from "@/hooks/use-auth";
import type { AgentAvatarStyleKey } from "@/lib/dicebear-styles";
import { useCommentAction } from "@/hooks/use-comment-action";
import { subscribeToTaskComments } from "@/lib/task-comment-bus";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { useCommentDictationClaim } from "@/desktop/use-comment-dictation-claim";
import { useDictation } from "@/desktop/use-dictation";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import { useComposerFileDrop } from "@/components/panels/use-composer-file-drop";
import { useComposerAttachmentLifetime } from "@/components/panels/use-composer-attachment-lifetime";
import {
  attachComposerFile,
  removeComposerAttachment,
} from "@/lib/composer-attachment";
import { CommentPopoverImages } from "./comment-popover-images";
import { useCommentPopoverResize } from "./use-comment-popover-resize";
import {
  CommentPopoverTaskList,
  type CommentPopoverFrameTask,
} from "./comment-popover-task-list";
import { useCommentPopoverDemoSubmit } from "./comment-popover-demo-submit";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { useInteractiveLayer } from "@/components/layout/interactive-layer";
import { CommentEditorField } from "./comment-editor-field";
import { useFrame } from "@/stores/frames-selectors";
import { useFrameScreenRect } from "@/lib/frame-screen-rect";

export function CommentPopover() {
  const t = useT(canvasCommentsMessages);
  const popover = useCanvasStore((s) => s.commentPopover);
  const closePopover = useCanvasStore((s) => s.closeCommentPopover);
  const currentProjectId = useProjectStore((s) => s.currentProject?.id);
  const currentSpaceId = useProjectStore((s) => s.currentSpace?.id);
  const projectAgents = useAgentStore((s) => s.agents);
  const allTasks = useTasksStore((s) => s.tasks);
  const { user } = useAuth();
  const avatarStyleKey = (user?.agent_avatar_style ??
    "glass") as AgentAvatarStyleKey;

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [frameTasks, setFrameTasks] = useState<CommentPopoverFrameTask[]>([]);
  const [attachedImages, setAttachedImages] = useState<ImageAttachment[]>([]);
  useComposerAttachmentLifetime(attachedImages, setAttachedImages);
  const sendLock = useRef(false);
  const dictationStatus = useDictation((s) => s.status);

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  const frame = useFrame(popover?.frameId);
  const shownTitle = frame?.title || popover?.frameTitle;
  const frameTitle = shownTitle || "Untitled";
  const frameType = frame?.frameType || "unknown";

  const comment = useCommentAction({
    frameId: popover?.frameId ?? "",
    frameTitle,
    frameType,
  });
  const popoverScopeMismatch =
    !!popover &&
    ((!!popover.projectId && popover.projectId !== currentProjectId) ||
      (!!popover.spaceId && popover.spaceId !== currentSpaceId));
  const popoverDisplayMismatch =
    popover?.displayId != null && popover.displayId !== getWindowDisplayId();
  const popoverReady =
    !!popover && !popoverScopeMismatch && !popoverDisplayMismatch;
  const popoverLayer = useInteractiveLayer("comment-popover", {
    active: popoverReady,
    baseZIndex: 70,
  });
  // Unscoped popovers sit 8px right of the frame's window or card, scoped
  // ones at their badge; with no rect here they stay where they last were.
  const liveRect = useFrameScreenRect(
    popoverReady && !popover.scopedTaskId ? popover.frameId : null,
  );
  const lastRect = useRef<{ for: object; rect: typeof liveRect }>(null);
  if (liveRect && popover) lastRect.current = { for: popover, rect: liveRect };
  const rect =
    liveRect ??
    (lastRect.current?.for === popover ? lastRect.current.rect : null);
  const anchor = rect
    ? { x: rect.left + rect.width + 8, y: rect.top }
    : popover?.position;
  const popoverPosition =
    popoverReady && anchor
      ? { x: anchor.x + offset.x, y: anchor.y + offset.y }
      : null;
  const commentTextRef = useRef(comment.text);
  commentTextRef.current = comment.text;
  const { surfaceRef, surfaceStyle, resizeHandleProps } =
    useCommentPopoverResize({ popover, popoverPosition });
  const onFile = useCallback((file: File) => {
    void attachComposerFile(file, setAttachedImages);
  }, []);
  const fileDrop = useComposerFileDrop({
    rootRef: surfaceRef,
    onFile,
    disabled:
      !popoverReady ||
      comment.isSending ||
      popover?.submitMode === "onboarding_demo_suppress",
  });

  useEffect(() => {
    if (popoverScopeMismatch) closePopover();
  }, [popoverScopeMismatch, closePopover]);

  const dispatchDemoSuppressedSubmit = useCommentPopoverDemoSubmit({
    popoverReady,
    popover,
    text: comment.text,
    selectedAgent: comment.selectedAgent,
    setAttachedImages,
    closePopover,
  });

  const commentDictation = useCommentDictationClaim({
    id: `frame-comment:${popover?.frameId}`,
    disableScreenshotCapture:
      popover?.submitMode === "onboarding_demo_suppress",
    deliverTranscript: (text, images) => {
      const current = commentTextRef.current.trimEnd();
      comment.handleTextChange(current ? `${current}\n${text}` : text);
      if (
        popover?.submitMode !== "onboarding_demo_suppress" &&
        images.length > 0
      ) {
        setAttachedImages((prev) => [...prev, ...images]);
      }
      requestAnimationFrame(() => inputRef.current?.focus());
    },
  });

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target?.closest(
          "button,input,textarea,select,a,[contenteditable='true']",
        )
      ) {
        return;
      }
      e.preventDefault();
      setDragging(true);
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        ox: offset.x,
        oy: offset.y,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [offset],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      setOffset({
        x: dragStart.current.ox + (e.clientX - dragStart.current.x),
        y: dragStart.current.oy + (e.clientY - dragStart.current.y),
      });
    },
    [dragging],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    setDragging(false);
    const target = e.target as HTMLElement;
    if (target.hasPointerCapture?.(e.pointerId))
      target.releasePointerCapture(e.pointerId);
  }, []);

  useEffect(() => {
    if (popoverReady) {
      setOffset({ x: 0, y: 0 });
      setAttachedImages([]);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [popover, popoverReady]);

  const { filteredAgents, selectAgent: selectCommentAgent } = comment;
  const autoInjectedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!popoverReady || !popover?.agentId || filteredAgents.length === 0)
      return;
    const key = `${popover.frameId}:${popover.agentId}`;
    if (autoInjectedForRef.current === key) return;
    const matched = filteredAgents.find((a) => a.id === popover.agentId);
    if (matched) {
      selectCommentAgent(matched);
      autoInjectedForRef.current = key;
    }
  }, [popoverReady, popover, filteredAgents, selectCommentAgent]);
  useEffect(() => {
    if (!popoverReady || !popover?.demoAgent) return;
    const key = `${popover.frameId}:${popover.demoAgent.id}`;
    if (autoInjectedForRef.current === key) return;
    if (comment.selectedAgent?.id === popover.demoAgent.id) return;
    selectCommentAgent(popover.demoAgent);
    autoInjectedForRef.current = key;
  }, [popoverReady, popover, comment.selectedAgent, selectCommentAgent]);
  useEffect(() => {
    if (!popover) autoInjectedForRef.current = null;
  }, [popover]);

  const setSelectedTaskId = comment.setSelectedTaskId;
  useEffect(() => {
    if (popoverReady && popover?.scopedTaskId) {
      setSelectedTaskId(popover.scopedTaskId);
    }
  }, [popover?.scopedTaskId, popoverReady, setSelectedTaskId]);

  useEffect(() => {
    if (!popoverReady || !popover || !currentProjectId) return;
    const frameId = popover.frameId;
    const scopedTaskId = popover.scopedTaskId;
    const tasks = scopedTaskId
      ? allTasks.filter((t) => t.id === scopedTaskId)
      : allTasks.filter((t) => t.frame === frameId);
    if (tasks.length === 0) {
      setFrameTasks([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const loaded: CommentPopoverFrameTask[] = await Promise.all(
        tasks.map(async (t) => {
          let comments: TaskComment[] = [];
          try {
            comments = await fetchComments(currentProjectId, t.id);
          } catch {
            /* best-effort */
          }
          return {
            id: t.id,
            title: t.title,
            status: t.status,
            assignedAgentName: t.assigned_agent_detail?.name ?? "",
            comments,
          };
        }),
      );
      if (!cancelled) setFrameTasks(loaded);
    })();
    return () => {
      cancelled = true;
    };
  }, [popoverReady, popover, currentProjectId, allTasks]);

  useEffect(() => {
    if (!popoverReady) return;
    const unsub = subscribeToTaskComments(({ task_id, comment }) => {
      setFrameTasks((prev) => {
        if (!prev.some((ft) => ft.id === task_id)) return prev;
        return prev.map((ft) => {
          if (ft.id !== task_id) return ft;
          if (ft.comments.some((c) => c.id === comment.id)) return ft;
          return { ...ft, comments: [...ft.comments, comment] };
        });
      });
    });
    return unsub;
  }, [popoverReady]);

  useEffect(() => {
    if (!popoverReady) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !comment.showMentions) closePopover();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [popoverReady, closePopover, comment.showMentions]);

  if (!popoverReady || !popoverPosition) return null;

  const canSend =
    !comment.isSending &&
    !attachedImages.some((image) => image.uploading || image.uploadError) &&
    (comment.canSend || attachedImages.some((image) => image.imageId));
  const handleSend = async () => {
    if (!canSend || sendLock.current) return;
    if (dispatchDemoSuppressedSubmit()) return;
    sendLock.current = true;
    const imageIds = attachedImages
      .filter((img) => img.imageId && !img.uploading)
      .map((img) => img.imageId!);
    try {
      const sent = await comment.submit({
        imageIds: imageIds.length > 0 ? imageIds : undefined,
      });
      if (!sent) return;
      setAttachedImages([]);
      closePopover();
    } finally {
      sendLock.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (comment.handleMentionKeyDown(e.key)) {
      e.preventDefault();
      return;
    }
    if (e.key === "Escape" && !comment.showMentions && comment.text.trim()) {
      e.preventDefault();
      e.stopPropagation();
      comment.setText("");
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend) void handleSend();
    }
  };

  return (
    <div
      className="pointer-events-auto fixed"
      style={{
        left: popoverPosition.x,
        top: popoverPosition.y,
        ...popoverLayer.style,
      }}
      {...popoverLayer.interactionProps}
      data-interactive="true"
      data-testid="comment-popover"
    >
      <div
        ref={surfaceRef}
        className={cn(
          surfaceRoleClassName("menu"),
          "relative flex flex-col p-3",
        )}
        style={surfaceStyle}
        {...fileDrop.dropProps}
        data-file-dragging={fileDrop.dragging || undefined}
        {...surfaceRoleAttributes("menu", {
          nativeLevel: "root",
          nativeGroup: "menus",
        })}
      >
        <div
          className={cn(
            "mb-2 flex items-center justify-between",
            dragging ? "cursor-grabbing" : "cursor-grab",
          )}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <span className="truncate text-appkit-small text-muted select-none">
            {shownTitle || t("untitled")}
          </span>
          <button
            type="button"
            onClick={closePopover}
            className="rounded p-0.5 text-muted transition-colors hover:text-secondary"
            data-testid="comment-popover-cancel"
          >
            <Icons.X className="h-3 w-3" />
          </button>
        </div>

        <CommentPopoverTaskList
          frameTasks={frameTasks}
          projectAgents={projectAgents}
        />

        <div className="relative flex min-h-0 flex-1 flex-col rounded-control border border-glass-stroke bg-glass-fill p-2">
          <CommentEditorField
            avatarStyleKey={avatarStyleKey}
            selectedAgent={comment.selectedAgent}
            onClearAgent={comment.clearAgent}
            text={comment.text}
            onTextChange={comment.handleTextChange}
            onKeyDown={handleKeyDown}
            onPaste={fileDrop.handlePaste}
            textareaRef={inputRef}
            showMentions={comment.showMentions}
            mentionOptions={comment.mentionOptions}
            mentionIndex={comment.mentionIndex}
            onMentionSelect={comment.selectMentionOption}
            rows={6}
            inputTestId="comment-popover-input"
            removeAgentTestId="comment-popover-remove-agent"
            mentionMenuTestId="comment-popover-mentions"
          />
          <CommentPopoverImages
            images={attachedImages}
            onRemove={(imageId) =>
              removeComposerAttachment(imageId, setAttachedImages)
            }
          />
        </div>

        <div className="mt-2 flex justify-end gap-1">
          <button
            type="button"
            onClick={() => void commentDictation.toggle()}
            disabled={dictationStatus === "processing"}
            className={cn(
              "rounded-control-sm px-2 py-1 text-appkit-mini font-semibold transition-colors",
              dictationStatus === "recording"
                ? "text-accent"
                : "text-muted hover:bg-glass-stroke-subtle hover:text-secondary",
              dictationStatus === "processing" &&
                "cursor-not-allowed opacity-40",
            )}
            data-testid="comment-popover-voice"
            aria-label={t("voiceInput")}
          >
            <Icons.Mic className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSend();
            }}
            disabled={!canSend}
            className={cn(
              "rounded-control-sm px-3 py-1 text-appkit-mini font-semibold transition-colors",
              canSend
                ? "bg-accent text-black"
                : "cursor-not-allowed text-muted",
            )}
            data-testid="comment-popover-submit"
          >
            {comment.isSending ? t("sending") : t("send")}
          </button>
        </div>
        <div
          className="absolute bottom-0 right-0 h-3 w-3 cursor-nwse-resize touch-none"
          data-testid="comment-popover-resize"
          {...resizeHandleProps}
        />
      </div>
    </div>
  );
}
